import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, MessageCircle, Sparkles } from "lucide-react";
import { Laptop } from "./Laptop";
import { waLink } from "@/lib/site";
import { useScrollTo } from "./SmoothScroll";

const LINES = [
  [{ t: "Transform Your" }],
  [{ t: "Business with a" }],
  [{ t: "Stunning", gold: true }, { t: " Website!" }],
];

const ease = [0.16, 1, 0.3, 1];

const MaskedHeading = () => (
  <h1 data-testid="hero-heading" className="font-display text-[2.6rem] font-semibold leading-[1.02] tracking-tight text-white sm:text-6xl lg:text-[4.6rem]">
    {LINES.map((line, i) => (
      <span key={i} className="block overflow-hidden pb-[0.08em]">
        <motion.span className="block" initial={{ y: "110%" }} animate={{ y: 0 }} transition={{ duration: 1.1, delay: 0.25 + i * 0.13, ease }}>
          {line.map((p) => (
            <span key={p.t} className={p.gold ? "text-gold-grad italic" : ""}>{p.t}</span>
          ))}
        </motion.span>
      </span>
    ))}
  </h1>
);

const Fade = ({ d, children, className }) => (
  <motion.div className={className} initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: d, ease }}>
    {children}
  </motion.div>
);

export const Hero = () => {
  const ref = useRef(null);
  const scrollTo = useScrollTo();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yLaptop = useTransform(scrollYProgress, [0, 1], [0, 70]);
  const yGlow = useTransform(scrollYProgress, [0, 1], [0, -120]);

  return (
    <section ref={ref} id="home" data-testid="hero-section" className="relative overflow-hidden pb-32 pt-32 sm:pt-40 lg:pb-28">
      <div className="grid-lines pointer-events-none absolute inset-0" />
      <motion.div style={{ y: yGlow }} className="pointer-events-none absolute -top-40 right-[-10%] h-[620px] w-[620px] rounded-full bg-royal/25 blur-[140px]" />
      <div className="pointer-events-none absolute bottom-0 left-[-10%] h-[420px] w-[420px] rounded-full bg-gold/10 blur-[120px]" />

      <div className="relative mx-auto grid max-w-7xl items-center gap-16 px-5 sm:px-8 lg:grid-cols-[1.05fr_1fr] lg:gap-10">
        <div>
          <Fade d={0.1}>
            <p data-testid="hero-eyebrow" className="glass mb-8 inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs font-medium text-slate-200">
              <Sparkles className="h-3.5 w-3.5 text-[var(--gold)]" />
              Your Vision, Our Design — Web Studio in Noida
            </p>
          </Fade>
          <MaskedHeading />
          <Fade d={0.75}>
            <p data-testid="hero-subheading" className="mt-8 max-w-xl text-base leading-relaxed text-[var(--muted)] md:text-lg">
              Professional Website Designing &amp; Development Services at Affordable Prices. We Help Businesses Grow Online with Beautiful, Fast, Mobile-Friendly &amp; SEO-Ready Websites.
            </p>
          </Fade>
          <Fade d={0.9} className="mt-10 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
            <a href="/#contact" onClick={(e) => scrollTo("contact", e)} data-testid="hero-quote-button" className="btn-gold btn-shine group inline-flex items-center justify-center gap-2 rounded-full px-7 py-4 text-sm font-semibold transition-transform duration-300 hover:-translate-y-0.5">
              Get a Free Quote <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </a>
            <a href={waLink()} target="_blank" rel="noopener noreferrer" data-testid="hero-whatsapp-button" className="inline-flex items-center justify-center gap-2 rounded-full border border-[#25D366]/50 bg-[#25D366]/10 px-7 py-4 text-sm font-semibold text-white transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-[#25D366]/20">
              <MessageCircle className="h-4 w-4 text-[#25D366]" /> Chat on WhatsApp
            </a>
            <a href="/#services" onClick={(e) => scrollTo("services", e)} data-testid="hero-services-button" className="inline-flex items-center justify-center gap-2 rounded-full px-5 py-4 text-sm font-semibold text-slate-200 underline-offset-8 transition-colors hover:text-[var(--gold)] hover:underline">
              Explore Our Services
            </a>
          </Fade>
          <Fade d={1.05}>
            <p className="mt-10 text-xs uppercase tracking-[0.3em] text-slate-400">We Build Websites That Build Your Business.</p>
          </Fade>
        </div>

        <motion.div style={{ y: yLaptop }} initial={{ opacity: 0, scale: 0.92, rotateX: 18 }} animate={{ opacity: 1, scale: 1, rotateX: 0 }} transition={{ duration: 1.4, delay: 0.4, ease }}>
          <Laptop />
        </motion.div>
      </div>
    </section>
  );
};
